import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tiptap-editor/tiptap-ui-primitive/tooltip/tooltip';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import {
    getEditorAllowedZyVersions,
    getEditorVersionFilter,
    isZyVersionNodeVisible,
    normalizeSelectedZyVersionFilter,
} from '@/lib/zy-tiptap-utils';
import type { ReactNodeViewProps } from '@tiptap/react';
import { NodeViewContent, useEditorState } from '@tiptap/react';
import { useCallback, useMemo, useState } from 'react';
import { ZyVersionBadge } from './zyversion-badge';
import { getVersionBadgeColors, getVersionColor } from './zyversion-color';

export function ZyVersionBlockNodeView(props: ReactNodeViewProps) {
    const { node, editor, updateAttributes, decorations } = props;
    const isEditorReadonly = useIsEditorReadonly(editor);
    const selectedZyVersion =
        useEditorState({
            editor,
            selector: ({ editor: ed }) => (ed ? getEditorVersionFilter(ed) : null),
        }) ?? null;
    const version = node.attrs.version ?? '';
    const allowedVersions =
        useEditorState({
            editor,
            selector: ({ editor: ed }) => (ed ? getEditorAllowedZyVersions(ed) : []),
        }) ?? [];
    const isVisible = isZyVersionNodeVisible(selectedZyVersion, version);
    const normalizedSelectedZyVersion = normalizeSelectedZyVersionFilter(selectedZyVersion);
    const isInlineView = normalizedSelectedZyVersion === null;
    const stringForColorGeneration = isInlineView ? version : (normalizedSelectedZyVersion?.join(',') ?? null);
    const badgeColors = useMemo(
        () => getVersionBadgeColors(isInlineView ? stringForColorGeneration : version),
        [stringForColorGeneration, isInlineView, version],
    );
    const backgroundColor = useMemo(
        () => getVersionColor(isInlineView ? stringForColorGeneration : version),
        [stringForColorGeneration, isInlineView, version],
    );

    const [isRevealedLocally, setIsRevealedLocally] = useState(false);
    const [isEditingVersion, setIsEditingVersion] = useState(false);
    const showContent = isVisible || isRevealedLocally || isEditingVersion;
    const showLocalRevealChrome = isRevealedLocally && !isVisible;

    const handleVersionChange = useCallback(
        (v: string) => {
            updateAttributes({ version: v });
            setIsRevealedLocally(false);
        },
        [updateAttributes],
    );

    const handleLocalRevealSurfaceClick = useCallback(() => {
        setIsRevealedLocally(false);
    }, []);

    const nodeView = (
        <ZyNodeViewWrapper
            decorations={decorations}
            as="div"
            className={`relative my-2 rounded ${showLocalRevealChrome && !isEditingVersion ? 'cursor-pointer' : ''}`}
            style={{ backgroundColor: showContent ? backgroundColor : undefined }}
            data-zy-version-block
            onClick={showLocalRevealChrome && !isEditingVersion ? handleLocalRevealSurfaceClick : undefined}
        >
            {!showContent && (
                <Tooltip
                    delay={200}
                    closeDelay={0}
                >
                    <TooltipTrigger asChild>
                        <div
                            contentEditable={false}
                            className="flex min-h-10 cursor-pointer items-center rounded border border-dashed border-gray-300 bg-gray-50/80 px-3 py-2 text-sm italic text-gray-500 transition-colors hover:bg-gray-100/90 dark:border-gray-600 dark:bg-gray-800/40 dark:text-gray-400 dark:hover:bg-gray-800/70"
                            role="button"
                            tabIndex={0}
                            aria-label={`Expand hidden ${version} content`}
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsRevealedLocally(true);
                            }}
                        >
                            <span
                                aria-hidden
                                className="mr-2 text-gray-400 dark:text-gray-500"
                            >
                                ···
                            </span>
                            <span>Hidden {version || 'other version'} content</span>
                            <span
                                aria-hidden
                                className="ml-2 text-gray-400 dark:text-gray-500"
                            >
                                ···
                            </span>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>{`Expand hidden ${version} content`}</TooltipContent>
                </Tooltip>
            )}
            <div
                className={showContent ? undefined : 'hidden'}
                aria-hidden={!showContent}
            >
                <span
                    contentEditable={false}
                    className="absolute -top-2.5 left-2 z-20 inline-flex items-center gap-0.5"
                >
                    <ZyVersionBadge
                        version={version}
                        allowedVersions={allowedVersions}
                        onVersionChange={isEditorReadonly ? undefined : handleVersionChange}
                        onVersionPopoverOpenChange={isEditorReadonly ? undefined : setIsEditingVersion}
                        colors={badgeColors}
                        emptyLabel="zyV"
                    />
                </span>
                <div className="p-4">
                    <NodeViewContent />
                </div>
            </div>
        </ZyNodeViewWrapper>
    );

    return showLocalRevealChrome ? (
        <Tooltip
            delay={200}
            closeDelay={0}
            open={isEditingVersion ? false : undefined}
        >
            <TooltipTrigger asChild>{nodeView}</TooltipTrigger>
            <TooltipContent>Hide content</TooltipContent>
        </Tooltip>
    ) : (
        nodeView
    );
}
